import { atom } from 'jotai';
import { isEmpty, isEqual } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

import {
  DEFAULT_TIME_FILTER,
  type TimePresetKeyWithField
} from '../TimeFilter';
import type { QBFilter, QBFiltersConfig } from './QueryBuilder.types';
import {
  mapQBFiltersToSearchParams,
  mapSearchParamsToQBFilters
} from './QueryBuilder.utils';

export const qbFiltersAtom = atom<QBFilter[] | null>(null);
// `null` is the "not yet restored" sentinel, so consumers know to wait
// before fetching (see useQueryBuilderParams's `isReady`).
export const qbTimeFilterAtom = atom<TimePresetKeyWithField | null>(null);

export type QBFilterNormalizer = (filters: QBFilter[]) => QBFilter[];

const hasAnyKnownParam = (
  searchParams: QBFilter[],
  config: QBFiltersConfig
) => {
  const configFields = Object.keys(config);

  return searchParams.some((f) => configFields.includes(f.field));
};

const readSessionStorage = (sessionKey: string | undefined) => {
  if (!sessionKey) return null;
  try {
    const raw = sessionStorage.getItem(sessionKey);
    if (!raw) return null;

    return mapSearchParamsToQBFilters(raw);
  } catch {
    sessionStorage.removeItem(sessionKey);
    return null;
  }
};

// This hook manages the synchronization of filter state between the URL, session storage, and Jotai atoms.
// Ideally it should be used at a high level (e.g. in Ticket page) with configuration and atoms should be used in other components.
// Returns `isReady`, true once filters are fully restored. Consumers driven by
// the filter/time atoms (grids, group-by queries) should gate on it to avoid
// fetching twice (once with defaults, once with restored values).
export const useQueryBuilderParams = ({
  config,
  isLoading,
  sessionKey,
  defaultFilter,
  normalizeFilters,
  setFilters,
  setTimeFilter
}: {
  config: QBFiltersConfig;
  isLoading: boolean;
  sessionKey?: string;
  defaultFilter?: QBFilter[];
  normalizeFilters?: QBFilterNormalizer;
  setFilters: (filters: QBFilter[]) => void;
  setTimeFilter: (timeFilter: TimePresetKeyWithField | null) => void;
}): { isReady: boolean } => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFilters = mapSearchParamsToQBFilters(searchParams.get('f') || '');
  const timeSearchParam = searchParams.get('t') as TimePresetKeyWithField;
  const normalizedSearchFilters = normalizeFilters
    ? normalizeFilters(searchFilters)
    : searchFilters;

  // ── One-time mount restoration ──────────────────────────────────────────
  const restoredRef = useRef(false);

  // Restores filters from session storage if URL doesn't have params
  const restoreFilterParams = () => {
    const saved = readSessionStorage(sessionKey);
    const nextFilters = normalizeFilters
      ? normalizeFilters(saved || defaultFilter || [])
      : saved || defaultFilter || [];
    let next = '{}';
    if (nextFilters.length > 0) {
      next = mapQBFiltersToSearchParams(nextFilters);
    }

    setSearchParams(
      (prev) => {
        prev.set('f', next);
        sessionStorage.setItem(sessionKey!, next);
        return prev;
      },
      { replace: true }
    );
  };

  // Restores time filter from session storage if URL doesn't have params
  const restoreTimeFilter = () => {
    const savedTime = sessionStorage.getItem(`${sessionKey}Time`);
    const nextTime = savedTime || DEFAULT_TIME_FILTER;

    setTimeFilter(nextTime as TimePresetKeyWithField);
    setSearchParams(
      (prev) => {
        prev.set('t', nextTime);
        sessionStorage.setItem(`${sessionKey}Time`, nextTime);
        return prev;
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (isLoading || isEmpty(config) || restoredRef.current) return;
    restoredRef.current = true;

    // handle filters restoration from URL → session storage → defaults (in that order)
    if (hasAnyKnownParam(normalizedSearchFilters, config)) {
      // URL already has params (bookmark / back navigation) — trust the URL
      setFilters(normalizedSearchFilters);
      if (!isEqual(normalizedSearchFilters, searchFilters)) {
        const nextURLParams = mapQBFiltersToSearchParams(
          normalizedSearchFilters
        );
        setSearchParams(
          (prev) => {
            prev.set('f', nextURLParams);
            if (sessionKey) sessionStorage.setItem(sessionKey, nextURLParams);
            return prev;
          },
          { replace: true }
        );
      }
    } else {
      // Try restore from session storage
      restoreFilterParams();
    }

    // handle time filter restoration from URL → session storage → default
    if (!timeSearchParam) {
      restoreTimeFilter();
    } else {
      setTimeFilter(timeSearchParam);
    }
  }, [
    config,
    isLoading,
    normalizedSearchFilters,
    searchFilters,
    sessionKey,
    setSearchParams
  ]);

  // ── URL → atom sync (handles browser back / forward) ───────────────────
  // Skips the initial render (before restoration runs) via restoredRef.
  useEffect(() => {
    if (!restoredRef.current || isEmpty(config)) return;

    // always ensure URL params are set
    const isMissingFilterParam = searchParams.get('f') === null;
    const isMissingTimeParam = searchParams.get('t') === null;
    if (isMissingFilterParam) restoreFilterParams();
    if (isMissingTimeParam) restoreTimeFilter();

    // Skip syncing until the restored params are in the URL, otherwise the atoms
    // briefly hold defaults and consumers (e.g. the grid datasource) fetch twice.
    if (isMissingFilterParam || isMissingTimeParam) return;

    const nextSearchFilters = mapSearchParamsToQBFilters(
      searchParams.get('f') || ''
    );
    const nextNormalizedFilters = normalizeFilters
      ? normalizeFilters(nextSearchFilters)
      : nextSearchFilters;

    if (!isEqual(nextNormalizedFilters, nextSearchFilters)) {
      const nextURLParams = mapQBFiltersToSearchParams(nextNormalizedFilters);
      setSearchParams(
        (prev) => {
          prev.set('f', nextURLParams);
          if (sessionKey) sessionStorage.setItem(sessionKey, nextURLParams);
          return prev;
        },
        { replace: true }
      );
    }
    setFilters(nextNormalizedFilters);
    setTimeFilter(
      (searchParams.get('t') as TimePresetKeyWithField) || DEFAULT_TIME_FILTER
    );
  }, [searchParams]); // only re-run when the URL actually changes

  // True once the filter/time atoms hold their final, restored values.
  const isReady =
    restoredRef.current &&
    searchParams.get('f') !== null &&
    searchParams.get('t') !== null;

  return { isReady };
};

// hook containing actions to manage filter state and sync with URL and session storage
export const useQBFilterActions = (
  sessionKey: string,
  normalizeFilters?: QBFilterNormalizer
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const syncFilters = useCallback(
    (nextFilters: QBFilter[]) => {
      const normalizedFilters = normalizeFilters
        ? normalizeFilters(nextFilters)
        : nextFilters;
      const nextURLParams = mapQBFiltersToSearchParams(normalizedFilters);
      setSearchParams(
        (prev) => {
          prev.set('f', nextURLParams);
          return prev;
        },
        { replace: false }
      );
      sessionStorage.setItem(sessionKey, nextURLParams);
    },
    [normalizeFilters, setSearchParams, sessionKey]
  );

  const updateFilters = useCallback(
    (updates: QBFilter[]) => {
      const searchFilters = mapSearchParamsToQBFilters(
        searchParams.get('f') || ''
      );
      syncFilters([...searchFilters, ...updates]);
    },
    [searchParams, syncFilters]
  );

  const removeFilter = useCallback(
    (field: string) => {
      const searchFilters = mapSearchParamsToQBFilters(
        searchParams.get('f') || ''
      );
      syncFilters(searchFilters.filter((f) => f.field !== field));
    },
    [searchParams, syncFilters]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        prev.set('f', '{}');
        return prev;
      },
      { replace: false }
    );
    sessionStorage.removeItem(sessionKey);
  }, [setSearchParams, sessionKey]);

  const setTimeFilter = (timeFilter: TimePresetKeyWithField) => {
    const { _t, ...rest } = Object.fromEntries(searchParams);
    setSearchParams(
      new URLSearchParams({
        ...rest,
        t: timeFilter
      })
    );
    sessionStorage.setItem(`${sessionKey}Time`, timeFilter);
  };

  return {
    removeFilter,
    clearFilters,
    setTimeFilter,
    updateFilters
  };
};

export default useQueryBuilderParams;
